import { PointArray } from '@editor/interfaces/attribute'
import { CameraProfile } from '@editor/interfaces/camera'
import { getCentroidOfPolygon3d } from '@editor/services/Points'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Vector3 } from 'three'

import { CameraProfileTarget } from './definitions'

export enum SelectionStage {
  DRAW = 'DRAW',
  ROTATE = 'ROTATE',
  FOCUS = 'FOCUS',
  SAVING = 'SAVING',
}

interface CameraProfileState {
  /**
   * Whether the camera profile is for an area or an item.
   * This determines where the camera profile will be saved in
   * the backend and how it will be applied in the viewer.
   */
  profileTarget: CameraProfileTarget

  /**
   * Selection stage.
   */
  selectionStage: SelectionStage

  /**
   * Saved camera profile.
   */
  cameraProfile?: CameraProfile

  /**
   * Camera target (center/pivot point).
   */
  target?: PointArray

  /**
   * Plane drawn by user, defined by 3 points.
   */
  drawnPlanePoints: PointArray[]

  /**
   * Preview plane derived from drawPlanePoints, defined by 3 points.
   */
  previewPlanePoints: PointArray[]

  /**
   * Dirty flag.
   */
  isDirty: boolean

  /**
   * Set after camera has been initially positioned based on point cloud.
   */
  initCompleted: boolean
}

export const initialState: CameraProfileState = {
  selectionStage: SelectionStage.DRAW,
  target: undefined,
  drawnPlanePoints: [],
  previewPlanePoints: [],
  isDirty: false,
  initCompleted: false,
  profileTarget: CameraProfileTarget.Area,
}

export const slice = createSlice({
  name: 'cameraProfile',
  initialState,
  reducers: {
    /**
     * -- General actions
     */
    setInitCompleted: (state, action: PayloadAction<boolean>) => {
      state.initCompleted = action.payload
    },
    setSelectionStage: (state, action: PayloadAction<SelectionStage>) => {
      state.selectionStage = action.payload
    },
    setCameraProfile: (state, action: PayloadAction<CameraProfile | undefined>) => {
      state.cameraProfile = action.payload
    },
    setTarget: (state, action: PayloadAction<PointArray>) => {
      state.target = action.payload
    },
    setProfileTarget: (state, action: PayloadAction<CameraProfileTarget>) => {
      state.profileTarget = action.payload
    },

    /**
     * -- Drawing actions
     */
    setPlanePoints: (state, action: PayloadAction<PointArray[]>) => {
      state.drawnPlanePoints = action.payload
    },
    addPlanePoint: (
      state,
      { payload: { point, isTouched } }: PayloadAction<{ point: PointArray; isTouched: boolean }>,
    ) => {
      state.isDirty = true
      state.drawnPlanePoints.push(point)

      if (state.drawnPlanePoints.length === (isTouched ? 3 : 4)) {
        state.target = getCentroidOfPolygon3d(state.drawnPlanePoints.map((p) => new Vector3(...p))).toArray()
        state.selectionStage = SelectionStage.ROTATE
      }
    },
    updateLastPlanePoint: (state, action: PayloadAction<PointArray>) => {
      // If there's no point yet, add it
      if (state.drawnPlanePoints.length === 0) {
        state.drawnPlanePoints.push(action.payload)
      }

      state.drawnPlanePoints[state.drawnPlanePoints.length - 1] = action.payload
    },
    updatePlanePoint: (state, action: PayloadAction<{ index: number; point: PointArray }>) => {
      state.drawnPlanePoints[action.payload.index] = action.payload.point
    },
    undoLastPlanePoint: (state) => {
      if (state.drawnPlanePoints.length === 0) return
      state.drawnPlanePoints.pop()
    },
    setPreviewPlanePoints: (state, action: PayloadAction<PointArray[]>) => {
      state.previewPlanePoints = action.payload
    },

    /**
     * -- Reset actions
     */
    resetWorkingState: (state) => {
      state.selectionStage = SelectionStage.DRAW
      state.target = undefined
      state.drawnPlanePoints = []
      state.previewPlanePoints = []
      state.isDirty = false
    },
    reset: () => initialState,
  },
})

export const {
  setInitCompleted,
  setSelectionStage,
  setCameraProfile,
  setTarget,
  setProfileTarget,

  setPlanePoints,
  addPlanePoint,
  updatePlanePoint,
  updateLastPlanePoint,
  undoLastPlanePoint,
  setPreviewPlanePoints,

  resetWorkingState,
  reset,
} = slice.actions

export default slice.reducer
